import React, { useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChatContext } from '../../context/ChatContext';
import { FiX, FiUserPlus, FiUserMinus } from 'react-icons/fi';
import { useToast } from '../commons/toast/ToastContext';
import axios from '../../axiosConfig';

const ChatParticipantsPanel = ({ chatRoom, userId, isTrainer, schoolId, onClose }) => {
    const { t } = useTranslation('chat');
    const { addParticipants, removeParticipant } = useContext(ChatContext);
    const toast = useToast();

    const [showAddForm, setShowAddForm] = useState(false);
    const [availableUsers, setAvailableUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const isAdmin = chatRoom.participants?.some(
        p => p.userId === userId && p.role === 'ADMIN'
    );

    const canManage = isTrainer && isAdmin &&
        chatRoom.type !== 'SCHOOL_ANNOUNCEMENT' &&
        chatRoom.type !== 'DIRECT';

    useEffect(() => {
        if (showAddForm && schoolId) {
            fetchAvailableUsers();
        }
    }, [showAddForm, schoolId]);

    const fetchAllOwners = async () => {
        const allOwners = [];
        let page = 0;
        let lastPage = false;
        while (!lastPage) {
            const res = await axios.get(`/api/v1/owner/all/${schoolId}?page=${page}&size=100`);
            allOwners.push(...(res.data?.content || []));
            lastPage = res.data?.last !== false;
            page++;
        }
        return allOwners.map(o => ({
            id: o.id, name: `${o.firstName} ${o.lastName}`, role: t('participants_panel.roleOwner')
        }));
    };

    const fetchAvailableUsers = async () => {
        try {
            setLoadingUsers(true);
            const existingUserIds = chatRoom.participants
                ?.filter(p => p.active !== false)
                ?.map(p => p.userId) || [];

            // Fetch trainers and owners from school
            const [trainersRes, owners] = await Promise.all([
                axios.get(`/api/v1/trainer/find-all/${schoolId}`),
                fetchAllOwners()
            ]);

            const trainers = (trainersRes.data || []).map(tr => ({
                id: tr.id,
                name: `${tr.firstName} ${tr.lastName}`,
                role: t('participants_panel.roleTrainer')
            }));

            const all = [...trainers, ...owners].filter(u => !existingUserIds.includes(u.id));
            setAvailableUsers(all);
        } catch (err) {
            console.error('Failed to fetch users:', err);
            toast.error(t('participants_panel.fetchUsersError'));
        } finally {
            setLoadingUsers(false);
        }
    };

    const handleAddParticipants = async () => {
        if (selectedUsers.length === 0) return;
        try {
            await addParticipants(chatRoom.id, selectedUsers);
            toast.success(t('participants_panel.participantsAdded'));
            setShowAddForm(false);
            setSelectedUsers([]);
        } catch (err) {
            toast.error(err.message);
        }
    };

    const handleRemoveParticipant = async (participantUserId) => {
        if (window.confirm(t('participants_panel.removeConfirm'))) {
            try {
                await removeParticipant(chatRoom.id, participantUserId);
                toast.success(t('participants_panel.participantRemoved'));
            } catch (err) {
                toast.error(err.message);
            }
        }
    };

    const toggleUserSelection = (uId) => {
        setSelectedUsers(prev =>
            prev.includes(uId) ? prev.filter(id => id !== uId) : [...prev, uId]
        );
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'ADMIN': return t('participants_panel.roleAdmin');
            case 'READ_ONLY': return t('participants_panel.roleReadOnly');
            default: return t('participants_panel.roleMember');
        }
    };

    const activeParticipants = chatRoom.participants?.filter(p => p.active !== false) || [];

    return (
        <div className="chat-panel-overlay" onClick={onClose}>
            <div className="chat-participants-panel" onClick={(e) => e.stopPropagation()}>
                <div className="chat-panel-header">
                    <h3>{t('participants_panel.title', { count: activeParticipants.length })}</h3>
                    <button className="chat-panel-close" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                {canManage && !showAddForm && (
                    <button
                        className="chat-add-participant-btn"
                        onClick={() => setShowAddForm(true)}
                    >
                        <FiUserPlus /> {t('participants_panel.addParticipants')}
                    </button>
                )}

                {showAddForm && (
                    <div className="chat-add-form">
                        <h4>{t('participants_panel.selectUsers')}</h4>
                        {loadingUsers ? (
                            <p>{t('loading')}</p>
                        ) : availableUsers.length === 0 ? (
                            <p>{t('participants_panel.allUsersInChat')}</p>
                        ) : (
                            <>
                                <div className="chat-user-select-list">
                                    {availableUsers.map(user => (
                                        <label key={user.id} className="chat-user-select-item">
                                            <input
                                                type="checkbox"
                                                checked={selectedUsers.includes(user.id)}
                                                onChange={() => toggleUserSelection(user.id)}
                                            />
                                            <span>{user.name}</span>
                                            <span className="chat-user-role-tag">{user.role}</span>
                                        </label>
                                    ))}
                                </div>
                                <div className="chat-add-form-actions">
                                    <button
                                        className="chat-btn chat-btn-primary"
                                        onClick={handleAddParticipants}
                                        disabled={selectedUsers.length === 0}
                                    >
                                        {t('participants_panel.addCount', { count: selectedUsers.length })}
                                    </button>
                                    <button
                                        className="chat-btn chat-btn-secondary"
                                        onClick={() => {
                                            setShowAddForm(false);
                                            setSelectedUsers([]);
                                        }}
                                    >
                                        {t('participants_panel.cancel')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                <div className="chat-participants-list">
                    {activeParticipants.map(participant => (
                        <div key={participant.id} className="chat-participant-item">
                            <div className="chat-participant-avatar">
                                {participant.firstName?.charAt(0) || '?'}
                            </div>
                            <div className="chat-participant-info">
                                <span className="chat-participant-name">
                                    {participant.firstName} {participant.lastName}
                                    {participant.userId === userId && ` (${t('you')})`}
                                </span>
                                <span className="chat-participant-role">
                                    {getRoleLabel(participant.role)}
                                </span>
                            </div>
                            {canManage && participant.userId !== userId && (
                                <button
                                    className="chat-remove-participant-btn"
                                    onClick={() => handleRemoveParticipant(participant.userId)}
                                    title={t('participants_panel.removeTitle')}
                                >
                                    <FiUserMinus />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default ChatParticipantsPanel;
